import MaterialIcon from '@components/common/MaterialIcon';
import { Spinner } from '@components/common/Spinner';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
  EInboxLoadStatus,
  type TInboxLoadState,
} from '../hooks/useInviteInboxes';

type TInviteInboxPickerProps = {
  loadState: TInboxLoadState;
  onRetry: () => void;
  selectedInboxIds: string[];
  onChange: (inboxIds: string[]) => void;
  labelClassName: string;
};

const toggleInboxId = (inboxIds: string[], inboxId: string): string[] => {
  if (inboxIds.includes(inboxId)) {
    return inboxIds.filter((selectedInboxId) => selectedInboxId !== inboxId);
  }

  return [...inboxIds, inboxId];
};

/**
 * Inbox choice for a new Live Chat Agent. Only a superAdmin sees it. The
 * caller loads the inboxes, so the list survives when the field hides.
 */
export const InviteInboxPicker: React.FC<TInviteInboxPickerProps> = ({
  loadState,
  onRetry,
  selectedInboxIds,
  onChange,
  labelClassName,
}) => {
  const { t } = useTranslation();

  if (loadState.status === EInboxLoadStatus.Failed) {
    return (
      <div className='sm:col-span-2'>
        <LabelT text={t('settings.agent.inboxes')} className={labelClassName} />
        <div className='mt-1 flex items-center gap-2'>
          <p className='flex items-start gap-1 text-tiny text-red-base'>
            <MaterialIcon
              icon='error_outline'
              classes={['text-sm', 'mt-0.5', '!leading-none']}
            />
            <span>{t('users.invite.inboxes-load-failed')}</span>
          </p>
          <ButtonT
            type='button'
            styling='outline-primary'
            size='xs'
            rounding='md'
            onClick={onRetry}>
            {t('users.invite.inboxes-retry')}
          </ButtonT>
        </div>
      </div>
    );
  }

  if (loadState.status === EInboxLoadStatus.Loading) {
    return (
      <div className='sm:col-span-2'>
        <LabelT text={t('settings.agent.inboxes')} className={labelClassName} />
        <div className='mt-1'>
          <Spinner size='sm' />
        </div>
      </div>
    );
  }

  const { inboxes } = loadState;

  return (
    <div className='sm:col-span-2'>
      <LabelT text={t('settings.agent.inboxes')} className={labelClassName} />
      {inboxes.length > 0 ? (
        <div className='mt-1 grid max-h-40 grid-cols-1 gap-x-3 gap-y-1.5 overflow-y-auto sm:grid-cols-2'>
          {inboxes.map((inbox) => (
            <CheckboxInputT
              key={inbox.identifier}
              id={`invite-user-inbox-${inbox.identifier}`}
              label={inbox.name}
              className='!my-0'
              checked={selectedInboxIds.includes(inbox.identifier)}
              onChange={() =>
                onChange(toggleInboxId(selectedInboxIds, inbox.identifier))
              }
            />
          ))}
        </div>
      ) : (
        <p className='mt-1 flex items-start gap-1 text-tiny text-amber-700'>
          <MaterialIcon
            icon='warning_amber'
            classes={['text-sm', 'mt-0.5', '!leading-none']}
          />
          <span>{t('settings.agent.no-inboxes')}</span>
        </p>
      )}
    </div>
  );
};

export default InviteInboxPicker;
